export type Marketplace = "shopee" | "lazada" | "tiktok";
export type StockStatus = "healthy" | "low_stock" | "out_of_stock" | "oversold";
export type SortKey =
  | "sku"
  | "product_name"
  | "on_hand"
  | "sellable"
  | "allocation_sellable"
  | "last_synced_at"
  | "status_code";
export type SortDir = "asc" | "desc";

export interface InventoryFilterState {
  marketplace?: Marketplace;
  status?: StockStatus;
  /** Spec §3.4: rows with ap_name IS NULL (GET /v1/sku?brand=unmapped). */
  brand?: "unmapped";
  search: string;
  sortKey?: SortKey;
  sortDir?: SortDir;
  offset: number;
  limit?: number;
}

/**
 * Allows passing `undefined` for optional keys without violating
 * `exactOptionalPropertyTypes: true`. Use instead of
 * `Partial<InventoryFilterState>` when a key may be explicitly set to
 * `undefined` (e.g. to clear a filter).
 */
export type FilterUpdate = {
  [K in keyof InventoryFilterState]?: InventoryFilterState[K] | undefined;
};

const MARKETPLACES = new Set<string>(["shopee", "lazada", "tiktok"]);
const STATUSES = new Set<string>(["healthy", "low_stock", "out_of_stock", "oversold"]);
const SORT_KEYS = new Set<string>([
  "sku",
  "product_name",
  "on_hand",
  "sellable",
  "allocation_sellable",
  "last_synced_at",
  "status_code",
]);

/** Type guard: true when `v` is one of the 7 whitelisted sort keys. */
export function isSortKey(v: unknown): v is SortKey {
  return typeof v === "string" && SORT_KEYS.has(v);
}

/** Type guard: true when `v` is a valid sort direction (`"asc"` | `"desc"`). */
export function isSortDir(v: unknown): v is SortDir {
  return v === "asc" || v === "desc";
}

/**
 * Parses TanStack Router's raw search record into a typed InventoryFilterState.
 * Used as the inventory route's `validateSearch` body. Invalid enum/sort values
 * are dropped (degrade to defaults).
 *
 * Sort params are read as camelCase (`sortKey`/`sortDir`) because TanStack
 * Router serializes the `InventoryFilterState` object keys verbatim into the
 * URL — the app writes `?sortKey=...&sortDir=...`, not snake_case.
 */
export function paramsToFilter(raw: Record<string, string | undefined>): InventoryFilterState {
  const rawOffset = Number(raw.offset ?? "0");
  const result: InventoryFilterState = {
    search: raw.search ?? "",
    offset: Number.isFinite(rawOffset) && rawOffset > 0 ? rawOffset : 0,
  };
  if (MARKETPLACES.has(raw.marketplace ?? "")) result.marketplace = raw.marketplace as Marketplace;
  if (STATUSES.has(raw.status ?? "")) result.status = raw.status as StockStatus;
  if (isSortKey(raw.sortKey)) result.sortKey = raw.sortKey;
  if (isSortDir(raw.sortDir)) result.sortDir = raw.sortDir;
  if (raw.brand === "unmapped") result.brand = "unmapped";
  return result;
}
